// Helper functions for MATDiscoveryTable
export function coerce(v: any) {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function normalizeKey(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function getExistingValueForCol(col: string, existingMat: any) {
  if (!existingMat) return undefined;
  if (Object.prototype.hasOwnProperty.call(existingMat, col)) return existingMat[col];
  const normCol = normalizeKey(col);
  for (const k of Object.keys(existingMat)) {
    if (normalizeKey(k) === normCol) return existingMat[k];
  }
  return undefined;
}

export function getFirstDiscoveredValueForCol(col: string, current: any) {
  // DataSourceStatus is at the top level of the discovery item
  if (normalizeKey(col) === normalizeKey('DataSourceStatus')) {
    return current?.data_source_status;
  }

  const rows = current?.discovered_rows || [];
  for (const r of rows) {
    const v = (r?.data || {})[col];
    if (v != null && String(v) !== '') return v;
  }
  return undefined;
}

export function copyFromRow(source: Record<string, any>, columns: string[]) {
  const next: Record<string, any> = {};
  columns.forEach((col) => {
    const val = source[col];
    next[col] = val != null ? coerce(val) : '';
  });
  return next;
}

export function getOptions(key: string, existingMat: any, current: any): string[] {
  // DataSourceStatus has fixed options
  if (normalizeKey(key) === normalizeKey('DataSourceStatus')) {
    return ['created', 'active', 'inactive', 'cancelled'];
  }

  const opts = new Set<string>();
  const existingVal = getExistingValueForCol(key, existingMat);
  if (existingVal != null) opts.add(coerce(existingVal));

  if (current?.discovered_rows) {
    current.discovered_rows.forEach((r: any) => {
      const d = r?.data || {};
      if (key in d) opts.add(coerce(d[key]));
    });
  }
  return Array.from(opts);
}

export interface DiscoveryPosition {
  index: number;
  after?: string;
  before?: string;
}

export function readDiscoveryPosition(params: URLSearchParams): DiscoveryPosition {
  const index = Math.max(0, Number(params.get('i') || 0));
  const after = params.get('after');
  if (after != null) return { index, after };
  const before = params.get('before');
  if (before != null) return { index, before };
  return { index };
}

export function discoveryEntryParams(position: DiscoveryPosition): Record<string, string | number> {
  if (position.after !== undefined) return { limit: 1, after: position.after };
  if (position.before !== undefined) return { limit: 1, before: position.before };
  return { limit: 1, offset: position.index };
}

export function nextPosition(position: DiscoveryPosition, currentKey: string): DiscoveryPosition {
  return { index: position.index + 1, after: currentKey };
}

export function previousPosition(
  position: DiscoveryPosition,
  currentKey?: string
): DiscoveryPosition {
  const index = Math.max(0, position.index - 1);
  const before = currentKey ?? position.after;
  return before !== undefined ? { index, before } : { index };
}

export function positionAfterCreating(
  position: DiscoveryPosition,
  createdKey: string,
  discoveredOnly: boolean
): DiscoveryPosition {
  return discoveredOnly ? { index: position.index, after: createdKey } : position;
}

export function withDiscoveryPosition(
  params: URLSearchParams,
  position: DiscoveryPosition
): URLSearchParams {
  const written = new URLSearchParams(params.toString());
  written.set('i', String(position.index));
  written.delete('after');
  written.delete('before');
  if (position.after !== undefined) written.set('after', position.after);
  else if (position.before !== undefined) written.set('before', position.before);
  return written;
}
